import { useState } from "react";
import { toast } from "sonner";
import { api } from "../../../lib/api";

const FineDetailModal = ({ fine, darkMode, onClose, onRespond }) => {
  const [loadingDoc, setLoadingDoc] = useState(false);

  const getInfractionLabel = (type) => {
    const labels = {
      'speeding': 'Excesso de Velocidade',
      'parking': 'Estacionamento Indevido',
      'red_light': 'Sinal Vermelho',
      'phone': 'Uso de Telemóvel',
      'seatbelt': 'Cinto de Segurança',
      'alcohol': 'Álcool/Drogas',
      'documentation': 'Documentação',
      'other': 'Outra'
    };
    return labels[type] || type || 'Não especificado';
  };

  const getStatusInfo = (status) => {
    const statusMap = {
      'pending_response': { label: 'Pendente Resposta', color: 'bg-red-500 text-white' },
      'awaiting_payment': { label: 'Aguarda Pagamento', color: 'bg-amber-500 text-white' },
      'salary_deduction_pending': { label: 'Dedução Salarial Pendente', color: 'bg-orange-500 text-white' },
      'paid': { label: 'Pago', color: 'bg-emerald-500 text-white' },
      'contested': { label: 'Contestado', color: 'bg-purple-500 text-white' },
      'cancelled': { label: 'Anulado', color: 'bg-slate-500 text-white' }
    };
    return statusMap[status] || { label: status, color: 'bg-slate-500 text-white' };
  };

  const handleViewDocument = async (doc) => {
    setLoadingDoc(true);
    try {
      const response = await api.get(`/hr/vehicle-fines/${fine.fine_id}/documents/${doc.document_id}`, {
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: doc.mime_type });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (e) {
      toast.error('Erro ao abrir documento');
    } finally {
      setLoadingDoc(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '-';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const statusInfo = getStatusInfo(fine.status);

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className={`rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto ${darkMode ? 'bg-slate-800' : 'bg-white'}`} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className={`p-6 border-b sticky top-0 ${darkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
          <div className="flex items-center justify-between">
            <div>
              <h2 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-slate-800'}`}>Detalhes da Coima</h2>
              <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Nº Auto: {fine.notification_number || fine.fine_id}</p>
            </div>
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusInfo.color}`}>
              {statusInfo.label}
            </span>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Main Info Grid */}
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <p className={`text-xs uppercase font-medium ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Tipo de Infração</p>
                <p className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-slate-800'}`}>{getInfractionLabel(fine.infraction_type)}</p>
              </div>
              <div>
                <p className={`text-xs uppercase font-medium ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Viatura</p>
                <p className={`font-mono font-bold text-blue-500`}>{fine.license_plate}</p>
                {fine.vehicle_info && <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>{fine.vehicle_info.brand} {fine.vehicle_info.model}</p>}
              </div>
              <div>
                <p className={`text-xs uppercase font-medium ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Data e Hora</p>
                <p className={darkMode ? 'text-white' : 'text-slate-800'}>{fine.fine_date} {fine.fine_time && `às ${fine.fine_time}`}</p>
              </div>
              <div>
                <p className={`text-xs uppercase font-medium ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Local</p>
                <p className={darkMode ? 'text-white' : 'text-slate-800'}>{fine.location || '-'}</p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <p className={`text-xs uppercase font-medium ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Valor da Coima</p>
                <p className={`text-3xl font-bold ${darkMode ? 'text-white' : 'text-slate-800'}`}>{fine.amount?.toFixed(2)} €</p>
                {fine.points_deducted > 0 && (
                  <p className="text-sm text-red-500 font-medium">-{fine.points_deducted} pontos na carta</p>
                )}
              </div>
              <div>
                <p className={`text-xs uppercase font-medium ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Entidade Autuante</p>
                <p className={darkMode ? 'text-white' : 'text-slate-800'}>{fine.authority || '-'}</p>
              </div>
              <div>
                <p className={`text-xs uppercase font-medium ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Prazo de Pagamento</p>
                <p className={`font-medium ${fine.payment_deadline ? 'text-red-500' : (darkMode ? 'text-slate-400' : 'text-slate-500')}`}>
                  {fine.payment_deadline || '-'}
                </p>
              </div>
            </div>
          </div>

          {/* Description */}
          {fine.infraction_description && (
            <div>
              <p className={`text-xs uppercase font-medium mb-2 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Descrição da Infração</p>
              <p className={`p-3 rounded-lg ${darkMode ? 'bg-slate-700/50 text-slate-300' : 'bg-slate-100 text-slate-700'}`}>
                {fine.infraction_description}
              </p>
            </div>
          )}

          {/* Documents Section */}
          <div>
            <p className={`text-xs uppercase font-medium mb-3 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Documentos Anexados</p>
            {fine.documents && fine.documents.length > 0 ? (
              <div className="space-y-2">
                {fine.documents.map((doc, idx) => (
                  <div key={doc.document_id || idx} className={`flex items-center justify-between p-3 rounded-lg ${darkMode ? 'bg-slate-700/50' : 'bg-slate-100'}`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        doc.mime_type?.includes('pdf') ? 'bg-red-100 text-red-600' : 'bg-blue-100 text-blue-600'
                      }`}>
                        {doc.mime_type?.includes('pdf') ? (
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                          </svg>
                        ) : (
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                        )}
                      </div>
                      <div>
                        <p className={`font-medium text-sm ${darkMode ? 'text-white' : 'text-slate-800'}`}>{doc.filename}</p>
                        <p className={`text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                          {formatFileSize(doc.file_size)} • {doc.document_type === 'auto_noticia' ? 'Auto de Notícia' : doc.document_type}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleViewDocument(doc)}
                      disabled={loadingDoc}
                      className="px-3 py-1.5 rounded-lg bg-blue-600 text-white text-sm hover:bg-blue-500 transition-colors disabled:opacity-50"
                    >
                      {loadingDoc ? 'A abrir...' : 'Visualizar'}
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className={`text-center py-6 rounded-lg ${darkMode ? 'bg-slate-700/30' : 'bg-slate-100'}`}>
                <p className={darkMode ? 'text-slate-500' : 'text-slate-400'}>Sem documentos anexados</p>
              </div>
            )}
          </div>

          {/* Notes */}
          {fine.notes && (
            <div>
              <p className={`text-xs uppercase font-medium mb-2 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Observações</p>
              <p className={`p-3 rounded-lg ${darkMode ? 'bg-slate-700/50 text-slate-300' : 'bg-slate-100 text-slate-700'}`}>
                {fine.notes}
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={`p-6 border-t sticky bottom-0 ${darkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className={`flex-1 px-4 py-2 rounded-lg transition-colors ${
                darkMode ? 'bg-slate-700 text-white hover:bg-slate-600' : 'bg-slate-200 text-slate-700 hover:bg-slate-300'
              }`}
            >
              Fechar
            </button>
            {fine.status === 'pending_response' && (
              <button
                onClick={onRespond}
                className="flex-1 px-4 py-2 rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-500 transition-colors"
              >
                Responder à Coima
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FineDetailModal;
